// Core development requests: what staff need BUILT, and what happened to each ask.
//
// ── HOW THIS DIFFERS FROM THE SUGGESTION BOX, AND WHY ─────────────────────
// The suggestion box is a culture channel: anonymous by default, free-form,
// answered in prose. A development request is an operational one: somebody
// cannot do part of their job without new functionality, and acting on it
// almost always needs follow-up questions. So requests are ALWAYS attributed —
// there is no anonymous path — and instead of an answered/considering pair the
// status ladder mirrors how development actually proceeds: it is new, somebody
// is weighing it, it is committed, it is being built, and then it shipped or it
// was declined with a reason.

export type DevRequestStatus =
  | "new" | "considering" | "planned" | "in_progress" | "shipped" | "declined";

export const STATUS_LABEL: Record<DevRequestStatus, string> = {
  new: "New",
  considering: "Considering",
  planned: "Planned",
  in_progress: "In progress",
  shipped: "Shipped",
  declined: "Declined",
};

/**
 * Which application of the suite the request is about. One channel for all of
 * them — a request needs to say where it lives before anybody can route it.
 * The column is plain varchar, so growing the suite is an edit here, not a
 * migration.
 */
export const REQUEST_APPS = [
  "Sunday State HQ",
  "Sunday State Hub",
  "Warehouse Kiosk",
  "MRP",
  "Wholesale storefront",
  "DTC storefront",
  "Other / not sure",
] as const;

/** Which company function the request serves. Business language, not modules. */
export const REQUEST_AREAS = [
  "Sales & CRM",
  "Wholesale",
  "Production & MRP",
  "Warehouse & Inventory",
  "People & HR",
  "Finance",
  "Content & Growth",
  "Other",
] as const;

export interface DevRequestFacts {
  id: string;
  requester_id: string;
  app: string | null;
  area: string | null;
  title: string;
  content: string;
  status: DevRequestStatus | string;
  admin_response: string | null;
  created_at: string;
  decided_at?: string | null;
}

export interface DevRequestInbox {
  /** Nobody has weighed these yet — the queue that must not silently grow. */
  waiting: DevRequestFacts[];
  /** Committed or being built: planned + in_progress. */
  committed: DevRequestFacts[];
  /** Finished, either way: shipped + declined, newest decision first. */
  closed: DevRequestFacts[];
}

const OPEN: ReadonlySet<string> = new Set(["new", "considering"]);
const COMMITTED: ReadonlySet<string> = new Set(["planned", "in_progress"]);

export function requestInbox(rows: DevRequestFacts[]): DevRequestInbox {
  const ts = (r: DevRequestFacts) => Date.parse(r.created_at) || 0;
  const decided = (r: DevRequestFacts) => Date.parse(r.decided_at ?? "") || ts(r);
  return {
    // Oldest first: the person who has waited longest is answered first.
    waiting: rows.filter(r => OPEN.has(r.status)).sort((a, b) => ts(a) - ts(b)),
    committed: rows.filter(r => COMMITTED.has(r.status)).sort((a, b) => ts(a) - ts(b)),
    closed: rows.filter(r => r.status === "shipped" || r.status === "declined")
      .sort((a, b) => decided(b) - decided(a)),
  };
}

export interface Verdict { allowed: boolean; reason?: string }

/**
 * Whether a status change may be saved.
 *
 * One hard rule: a DECLINE carries an explanation, always. The requester put
 * their name on an operational need; "declined" with no reason reads as
 * "ignored", and the next request never gets written. Everything else is the
 * admin's judgment — statuses may move in any direction, because reality does
 * (a shipped feature regresses, a declined ask becomes viable).
 */
export function decisionCheck(
  current: DevRequestStatus | string,
  next: DevRequestStatus,
  response: string,
): Verdict {
  if (current === next) return { allowed: false, reason: "It is already in that state." };
  if (next === "declined" && !response.trim()) {
    return { allowed: false, reason: "Say why. A decline with no reason reads as \"ignored\", and it kills the channel." };
  }
  return { allowed: true };
}

/** What the requester's notification says when a decision lands. */
export function decisionNotice(title: string, next: DevRequestStatus): string {
  const what = title.length > 60 ? `${title.slice(0, 57)}…` : title;
  switch (next) {
    case "considering": return `Your dev request "${what}" is being considered.`;
    case "planned": return `Your dev request "${what}" is on the plan. 🎯`;
    case "in_progress": return `Your dev request "${what}" is being built. 🔨`;
    case "shipped": return `Your dev request "${what}" has shipped! 🚀`;
    case "declined": return `Your dev request "${what}" was declined — the reason is on the request.`;
    default: return `Your dev request "${what}" was updated.`;
  }
}
